import styled, { createGlobalStyle } from 'styled-components';
import { toRem, baseVariables as theme } from '@marriott/mi-ui-library';
import { chatKeyboardFocusVisible } from '../../styles/chatFocus';

/* ──────────────────────────────────────────
   Portal dropdown — rendered directly on body
   to escape all overflow:hidden ancestors.
   Positioned via JS-computed top/left from DOMRect.
────────────────────────────────────────── */
export const StyledPortalDropdown = styled.div<{
  $top: number;
  $left?: number;
  $right?: number;
  $panel: string;
  $isMobile?: boolean;
  $openUpward?: boolean;
  $maxHeight?: number;
}>`
  position: fixed;
  z-index: 9999;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => ($left !== undefined ? `${$left}px` : 'auto')};
  right: ${({ $right }) => ($right !== undefined ? `${$right}px` : 'auto')};

  /* Viewport-safe height: never spill below the fold */
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : `calc(100vh - 16px)`)};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* ── Datepicker panel ── */
  ${({ $panel }) =>
    $panel === 'dates'
      ? `
    background: transparent;
    box-shadow: none;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }

    .shop-datepicker-modal {
      position: static !important;
      left: auto !important;
      right: auto !important;
      top: auto !important;
      transform: none !important;
      width: ${toRem(712)};
    }

    .m-dropdown-container {
      position: static !important;
      width: auto !important;
      height: auto !important;
      max-width: none !important;
      background: transparent !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      transform: none !important;
      inset: auto !important;
    }

    .shop-datepicker-dropdown,
    .shop-datepicker-dropdown-styles {
      background: ${theme.color.base20};
      border-radius: ${toRem(12)};
      overflow: visible;
    }

    .datepicker-dropdown-wrapper,
    .date-picker-wrapper {
      overflow: visible;
    }

    .m-dropdown-content {
      padding: ${toRem(24)} ${toRem(24)} !important;
      max-height: none !important;
      min-height: unset !important;
      overflow: visible !important;
    }

    .m-dropdown-header,
    .mobile-dropdown-header,
    [class*="modal-backdrop"],
    [class*="overlay"] {
      display: none !important;
    }
  `
      : ''}

  /* ── Rooms & Rates panels ── */
  ${({ $panel }) =>
    $panel === 'rooms' || $panel === 'rate'
      ? `
    background: ${theme.color.base20};
    border-radius: ${toRem(12)};
    box-shadow: 0 ${toRem(7)} ${toRem(20)} 0 rgba(0, 0, 0, 0.25);
    min-width: ${toRem(300)};
    max-width: ${toRem(400)};
    /* overflow: hidden already set on the base; children use flex to fill */

    .m-dropdown-container {
      position: static !important;
      width: auto !important;
      height: auto !important;
      max-width: none !important;
      transform: none !important;
      inset: auto !important;
      background: transparent !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      /* Stretch to fill the constrained StyledPortalDropdown height */
      display: flex !important;
      flex-direction: column !important;
      flex: 1 1 auto !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }

    .m-dropdown-content {
      /* Scrollable content area — grows to fill available space */
      flex: 1 1 auto !important;
      height: auto !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;

      &::-webkit-scrollbar {
        display: none !important;
      }
    }

    .m-dropdown-action-bar {
      /* Pinned footer — never scrolls away */
      position: static !important;
      flex-shrink: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      padding: ${toRem(16)} ${toRem(20)} ${toRem(12)} !important;
      background: ${theme.color.base20} !important;
      border-radius: 0 0 ${toRem(12)} ${toRem(12)} !important;
      z-index: auto !important;

      .m-dropdown-action-clear,
      .clear {
        font-size: ${toRem(13)} !important;
        font-weight: 400 !important;
        color: ${theme.color.base10} !important;
        text-decoration: underline !important;
        background: none !important;
        border: none !important;
        padding: 0 !important;
        cursor: pointer !important;
      }

      /* WCAG 4.1.2: DropDownModal keeps an empty Clear in DOM when clearButtonLabel is omitted */
      button.m-dropdown-action-clear[tabindex='-1']:empty {
        visibility: hidden !important;
      }

      .apply-button {
        height: ${toRem(32)} !important;
        padding: 0 ${toRem(20)} !important;
        font-size: ${toRem(14)} !important;
        font-weight: 600 !important;
        border-radius: ${toRem(100)} !important;
        border: ${toRem(1.5)} solid ${theme.color.base10} !important;
        background: ${theme.color.base20} !important;
        color: ${theme.color.base10} !important;
        cursor: pointer !important;
        white-space: nowrap !important;

        &:hover {
          background: ${theme.color.base10} !important;
          color: ${theme.color.base20} !important;
        }
      }
    }

    .m-dropdown-header,
    .mobile-dropdown-header,
    [class*="modal-backdrop"],
    [class*="overlay"] {
      display: none !important;
    }
  `
      : ''}

  /* ── Shared: NG molecule resets ── */
  .ng-datepicker-wrap,
  .datepicker-block {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
  }

  [data-mini-trigger='rooms'],
  [data-mini-trigger='rates'],
  .rooms-and-guests > .m-input-text-field,
  .rooms-guests-input-wrapper,
  .special-rates-block > .m-input-text-field,
  .special-rates-block .special-rates-input-wrapper,
  .special-rates-block .special-rates-tooltip-container {
    display: none !important;
  }

  .dropdown-container {
    position: static !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: ${toRem(8)} ${toRem(8)} ${toRem(12)} !important;
    max-height: none !important;
    /* Participate in the flex-column layout so the action bar stays pinned */
    flex: 1 1 auto !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;

    &::-webkit-scrollbar {
      display: none !important;
    }
  }

  /* Stepper rows */
  .stepper-text-contianer {
    display: block;
    padding: ${toRem(12)} 0;
    border-bottom: ${toRem(1)} solid ${theme.color.neutral20};
    @media ${theme.mediaQuery.lg} {
      padding: ${toRem(8)} 0;
    }

    &:last-of-type {
      border-bottom: none;
    }
  }

  .m-stepper-wrapper {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: ${toRem(16)};

    .m-stepper-label,
    .stepper-label {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: ${toRem(2)};

      .t-font-s {
        font-size: ${toRem(14)};
        font-weight: ${theme.font.fontWeightRegular};
        color: ${theme.color.base10};
        line-height: 1.5;
      }

      .m-stepper-desc {
        font-size: ${toRem(12)};
        color: ${theme.color.neutral40};
        line-height: 1.5;
      }
    }
  }

  /* Child ages collection */
  .stepper-text-contianer:has(.child-collection) {
    border-bottom: none;
    padding-bottom: 0;

    > [data-component-name='m-ui-library-StepperTextBlock'] {
      padding-bottom: ${toRem(12)};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral20};
    }
  }

  .child-collection {
    width: 100%;
    display: flex;
    flex-direction: column;

    .stepper-text-contianer {
      display: block;
      padding: ${toRem(12)} 0;
      border-bottom: ${toRem(1)} solid ${theme.color.neutral20};

      &:last-of-type {
        border-bottom: ${toRem(1)} solid ${theme.color.neutral20};
      }
    }
  }

  .mobile-dropdown-header,
  .mobile-dropdown-footer {
    display: none;
  }

  @media (max-width: 991px) {
    .mobile-dropdown-header,
    .mobile-dropdown-footer {
      display: flex;
    }
  }

  @media ${theme.mediaQuery.lg} {
    .dropdown-apply-btn-container,
    .apply-button-desktop {
      display: none;
    }
  }
`;

export const StyledMiniSearchBar = styled.div`
  width: 100%;

  .mini-search-pill {
    /* Grid gives truly content-independent column widths.
       1fr columns never resize regardless of what text is in each segment. */
    display: grid;

    grid-template-columns: 1fr auto 1fr auto 1fr;
    align-items: stretch;
    background: ${theme.color.base20};
    border: ${toRem(1)} solid ${theme.color.neutral20};
    border-radius: ${toRem(9999)};
    overflow: hidden;
    height: ${toRem(56)};
    cursor: pointer;
    position: relative;
    width: 100%;
    min-width: 0;
    @media (min-width: 992px) {
      max-width: 400px;
    }

    .is-active + .mini-search-divider,
    .mini-search-divider:has(+ .is-active) {
      opacity: 0;
    }
  }

  .mini-search-segment {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 ${toRem(16)};
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${theme.color.base10};
    font-size: ${toRem(14)};
    font-weight: ${theme.font.fontWeightRegular};
    line-height: 1.2;
    position: relative;
    /* Width is set by the grid track (1fr) — never content-driven */
    min-width: 0;
    overflow: hidden;

    ${chatKeyboardFocusVisible}

    &.is-active .segment-value {
      font-weight: ${theme.font.fontWeightMedium};
    }

    .segment-value {
      font-weight: ${theme.font.fontWeightRegular};
      color: ${theme.color.base10};
      display: block;
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      &.has-value {
        font-weight: ${theme.font.fontWeightMedium};
      }

      &.no-value {
        color: ${theme.color.neutral40};
        font-weight: ${theme.font.fontWeightRegular};
      }
    }
  }

  .mini-search-divider {
    width: ${toRem(1)};
    height: ${toRem(24)};
    margin-top: ${toRem(16)};
    background: ${theme.color.neutral20};
    flex-shrink: 0;
    @media (max-width: 991px) {
      height: ${toRem(16)};
      margin-top: ${toRem(12)};
    }
  }

  .mini-search-bar-root {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    @media ${theme.mediaQuery.lg} {
      width: 100%;
    }
  }

  /* ── Loading state ── */
  &.is-loading .mini-search-pill {
    pointer-events: none;
    cursor: default;
  }

  .mini-search-divider--hidden {
    background: transparent;
  }

  .mini-search-skeleton-bar {
    display: block;
    height: ${toRem(16)};
    width: ${toRem(80)};
    border-radius: ${toRem(4)};
    flex-shrink: 0;
  }

  .mini-search-skeleton-bar--wide {
    width: ${toRem(80)};
  }

  /* ── Tablet (992px–1199px): tighter padding and font ── */
  @media (min-width: 992px) and (max-width: 1199px) {
    .mini-search-pill {
      height: ${toRem(56)};
    }

    .mini-search-segment {
      padding: 0 ${toRem(10)};
      font-size: ${toRem(13)};
    }
  }

  /* ── Mobile (<992px) ── */
  @media (max-width: 991px) {
    .mini-search-pill {
      display: flex;
      flex: 1;
      width: auto;
      height: ${toRem(40)};
    }

    .mini-search-segment {
      flex: 1 1 0;
      min-width: 0;
      max-width: none;
      padding: 0 ${toRem(8)};
      font-size: ${toRem(12)};

      .segment-value {
        width: 100%;
      }
    }

    .mini-search-segment--dates,
    .mini-search-segment--rate {
      max-width: none;
    }

    .mini-search-skeleton-bar,
    .mini-search-skeleton-bar--wide {
      width: 70%;
      max-width: ${toRem(80)};
    }
  }
`;

export const MobileSearchFormGlobalStyles = createGlobalStyle`
  /* WCAG 4.1.2: unnamed Clear control when reset is hidden (mi-ui-library + empty clearButtonLabel) */
  .party-mix-dropdown-container .m-dropdown-action-bar button.m-dropdown-action-clear[tabindex='-1']:empty {
    visibility: hidden !important;
  }

  /* ── All mobile modals: consistent 80vh bottom sheet ── */
  @media (max-width: 991px) {
    .dropdown-to-modal-background {
      z-index: 99999 !important;
    }

    .dropdown-to-modal {
      z-index: 100000 !important;
      height: 80vh !important;
      max-height: 80vh !important;
      position: fixed !important;
      bottom: 0 !important;
      top: auto !important;
      left: 0 !important;
      right: 0 !important;
      border-radius: ${toRem(16)} ${toRem(16)} 0 0 !important;
      padding: ${toRem(24)} ${toRem(16)} 0 ${toRem(16)} !important;
      overflow: hidden !important;
    }

    .dropdown-to-modal .m-dropdown-header {
      .header-heading {
        font-size: ${toRem(18)} !important;
        font-weight: 700 !important;
        margin: 0 !important;
      }

      .icon-clear {
        font-size: ${toRem(24)} !important;
      }
    }

    /* Mini Search mobile sheets (dates / rooms / rates) render via portal on body. iOS WebKit:
       mi-ui-library gives .m-dropdown-content { position: relative; z-index: 1 }, which can trap
       stacking vs the map. Static + auto z-index keeps the sheet paint order consistent with
       .dropdown-to-modal (z-index 100000) above the backdrop. */
    .dropdown-to-modal .m-dropdown-content {
      position: static !important;
      z-index: auto !important;
      height: calc(80vh - ${toRem(140)}) !important;
      max-height: calc(80vh - ${toRem(140)}) !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      padding: 0 !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;

      &::-webkit-scrollbar {
        display: none !important;
      }
    }

    .dropdown-to-modal .m-dropdown-action-bar {
      position: fixed !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      background: ${theme.color.base20} !important;
      padding: ${toRem(16)} ${toRem(24)} !important;
      padding-bottom: max(${toRem(16)}, env(safe-area-inset-bottom)) !important;
      border-top: none !important;
      z-index: 10 !important;
      border-radius: 0 !important;
    }
  }

  /* ── Datepicker mobile ── */
  @media (max-width: 991px) {
    .date-picker-mobile-wrapper .shop-datepicker-modal .dropdown-to-modal {
      height: 80vh !important;
      max-height: 80vh !important;

      .m-dropdown-content {
        height: calc(80vh - ${toRem(140)}) !important;
        max-height: calc(80vh - ${toRem(140)}) !important;
      }
    }

    .date-picker-mobile-wrapper .tab-wrapper {
      position: sticky !important;
      top: ${toRem(70)} !important;
      z-index: 4 !important;
      background: ${theme.color.base20} !important;
      padding-top: 0 !important;
      margin-top: 0 !important;

      ul {
        margin-top: 115px !important;
      }
    }

    .date-picker-mobile-wrapper .datepicker-calendar-wrapper,
    .date-picker-mobile-wrapper .datepicker-calendar-wrapper-mobile {
      margin-top: ${toRem(16)} !important;
      padding-bottom: ${toRem(80)} !important;
      padding-top: ${toRem(144)} !important;
    }
  }

  /* ── Rooms & Guests mobile ── */
  @media (max-width: 991px) {
    .rooms-and-guests .dropdown-to-modal {
      height: 80vh !important;
      max-height: 80vh !important;
      overflow: hidden !important;

      .m-dropdown-content {
        height: calc(80vh - ${toRem(140)}) !important;
        max-height: calc(80vh - ${toRem(140)}) !important;
        overflow-y: auto !important;
        padding: 0 !important;
      }

      .m-dropdown-children {
        padding-bottom: ${toRem(80)} !important;
      }

      .child-collection {
        padding-bottom: 0 !important;
      }
    }
  }

  /* ── Special Rates mobile ── */
  @media (max-width: 991px) {
    .special-rates-block .dropdown-to-modal,
    .tabbed-dropdown-container .dropdown-to-modal {
      height: 80vh !important;
      max-height: 80vh !important;

      .m-dropdown-content {
        height: calc(80vh - ${toRem(140)}) !important;
        max-height: calc(80vh - ${toRem(140)}) !important;
        overflow-y: auto !important;
        padding: ${toRem(0)} !important;
      }

      .m-dropdown-children {
        padding-bottom: ${toRem(80)} !important;
      }
    }
  }

  /* ── Intermediate form modal (field preview + Search button) ── */
  .mobile-search-form {
    &.dropdown-to-modal-background {
      z-index: 99999 !important;
    }

    .dropdown-to-modal {
      z-index: 100000 !important;
      height: 80vh !important;
      max-height: 80vh !important;
      border-radius: ${toRem(16)} ${toRem(16)} 0 0 !important;
      padding: ${toRem(24)} ${toRem(16)} 0 ${toRem(16)} !important;
      bottom: 0 !important;
      top: auto !important;

      .m-dropdown-content {
        height: calc(80vh - ${toRem(80)}) !important;
        max-height: calc(80vh - ${toRem(80)}) !important;
        padding: 0 !important;
        overflow-y: auto !important;
      }

      .m-dropdown-children {
        height: auto !important;
        padding-bottom: ${toRem(100)} !important;
      }
    }

    .m-dropdown-header {
      padding: 0 0 ${toRem(8)} 0 !important;
      position: sticky !important;
      top: 0 !important;
      background: ${theme.color.base20} !important;
      z-index: 1 !important;

      &:after {
        display: none !important;
      }
    }

    /* Three field rows */
    .mobile-form-fields {
      display: flex;
      flex-direction: column;
      padding-bottom: ${toRem(80)};
    }

    .mobile-form-field {
      cursor: pointer;

      .m-input-text-field {
        width: 100%;
        margin-bottom: ${toRem(16)};
        cursor: pointer;

        label { cursor: pointer; }
        input  { cursor: pointer; pointer-events: none; }
      }

      &:focus-visible {
        outline: ${toRem(2)} solid ${theme.color.base10};
        outline-offset: ${toRem(-2)};
        border-radius: ${toRem(4)};
      }
    }

    /* Fixed Search button at bottom */
    .mobile-form-cta {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: ${toRem(16)} ${toRem(24)};
      padding-bottom: max(${toRem(16)}, env(safe-area-inset-bottom));
      background: ${theme.color.base20};
      border-top: ${toRem(1)} solid ${theme.color.neutral20};
      z-index: 10;

      .find-hotels-btn {
        width: 100%;
        height: ${toRem(48)};
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${toRem(8)};
        font-size: ${toRem(16)};
        font-weight: ${theme.font.fontWeightMedium};
        border-radius: ${toRem(100)};

        .icon-search { font-size: ${toRem(18)}; }
      }
    }
  }

  #mini-search-form-modal {
    &.dropdown-to-modal-background { z-index: 99999 !important; }
    .dropdown-to-modal { z-index: 100000 !important; }
  }
`;
